import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface Depoimento {
  texto: string;
  nome: string;
  cargo: string;
  avatar: string;
  estrelas: number;
}

interface Etapa {
  numero: string;
  icone: string;
  titulo: string;
  subtitulo: string;
  descricao: string;
  destaque: string;
  cta: { label: string; href: string };
  imagem: string;
  cor: string;
  bgCard: string;
  borderCard: string;
  tag: string;
  depoimento: Depoimento;
}

const etapas: Etapa[] = [
  {
    numero: '01',
    icone: 'ri-quill-pen-line',
    titulo: 'Escreva sua Carta',
    subtitulo: 'Para o Eu do Futuro',
    descricao:
      'Coloque no papel quem você quer se tornar. Uma carta poderosa ao seu eu futuro ativa a mente inconsciente e alinha seus valores, desejos e identidade com o caminho que você quer trilhar.',
    destaque: 'A escrita transforma intenção em realidade.',
    cta: { label: 'Escrever minha carta', href: '/escrever-carta' },
    imagem:
      'https://public.readdy.ai/ai/img_res/14afeabde2afe25435cf3919d738328b.jpg',
    cor: 'from-gold-600 to-amber-500',
    bgCard: 'from-gold-500/8 to-gold-300/4',
    borderCard: 'border-gold-500/20',
    tag: 'Passo 1',
    depoimento: {
      texto: 'Quando abri minha carta 6 meses depois, eu já era essa mulher. A escrita me deu direção quando tudo parecia incerto — e foi exatamente o que aconteceu.',
      nome: 'Camila R.',
      cargo: 'Diretora de Marketing · São Paulo',
      avatar: 'https://readdy.ai/api/search-image?query=confident%20professional%20brazilian%20woman%2038%20dark%20hair%20minimal%20elegant%20portrait%20smile%20white%20background%20studio&width=80&height=80&seq=dep-carta-camila-v1&orientation=squarish',
      estrelas: 5,
    },
  },
  {
    numero: '02',
    icone: 'ri-brain-line',
    titulo: 'Pratique PNL',
    subtitulo: 'Reprogramação Mental Profunda',
    descricao:
      'Com técnicas validadas de Programação Neurolinguística, você dissolve crenças limitantes, ancora estados de alta performance e instala novos padrões mentais que sustentam sua transformação.',
    destaque: 'A mente reprogramada age diferente.',
    cta: { label: 'Ver exercícios de PNL', href: '/exercicios-pnl' },
    imagem:
      'https://readdy.ai/api/search-image?query=elegant%20professional%20woman%2040s%20eyes%20closed%20meditating%20in%20luxury%20dark%20room%20with%20warm%20golden%20light%20rays%20around%20her%20head%20crown%20chakra%20activation%20mental%20reprogramming%20NLP%20hypnosis%20calm%20focus%20power%20inner%20transformation%20editorial%20photography%20premium%20cinematic&width=600&height=720&seq=processo-pnl-step2-v2&orientation=portrait',
    cor: 'from-rose-500 to-pink-500',
    bgCard: 'from-rose-500/8 to-pink-500/4',
    borderCard: 'border-rose-500/20',
    tag: 'Passo 2',
    depoimento: {
      texto: 'Em três sessões de ancoragem eliminei o padrão de autossabotagem que me acompanhava há 12 anos. Não acreditei até acontecer comigo. É ciência aplicada ao comportamento.',
      nome: 'Fernanda M.',
      cargo: 'Empresária · Belo Horizonte',
      avatar: 'https://readdy.ai/api/search-image?query=successful%20businesswoman%2042%20natural%20beauty%20confident%20smile%20soft%20elegant%20portrait%20minimal%20background%20warm%20light&width=80&height=80&seq=dep-pnl-fernanda-v1&orientation=squarish',
      estrelas: 5,
    },
  },
  {
    numero: '03',
    icone: 'ri-sparkling-2-line',
    titulo: 'Seja Sua Melhor Versão',
    subtitulo: 'Identidade. Poder. Resultado.',
    descricao:
      'Quando mente e ação se alinham, a transformação acontece naturalmente. Você passa a agir, sentir e atrair resultados consistentes com a versão mais elevada de si mesma — na carreira, nos relacionamentos e na vida.',
    destaque: 'Você não muda o que faz. Muda quem você é.',
    cta: { label: 'Começar agora', href: '/quiz' },
    imagem:
      'https://readdy.ai/api/search-image?query=confident%20powerful%20elegant%20woman%2040s%20standing%20tall%20in%20luxury%20penthouse%20office%20panoramic%20city%20view%20golden%20sunset%20light%20streaming%20through%20floor%20to%20ceiling%20windows%20premium%20tailored%20blazer%20radiant%20empowered%20successful%20transformation%20achievement%20editorial%20fashion%20photography&width=600&height=720&seq=processo-versao-step3-v2&orientation=portrait',
    cor: 'from-emerald-500 to-teal-500',
    bgCard: 'from-emerald-500/8 to-teal-500/4',
    borderCard: 'border-emerald-500/20',
    tag: 'Passo 3',
    depoimento: {
      texto: 'Faturei 3x mais nos 90 dias após o método. Mas o que mais mudou foi como me vejo. Pela primeira vez na vida me sinto completamente digna do sucesso que construí.',
      nome: 'Larissa V.',
      cargo: 'CEO & Fundadora · Florianópolis',
      avatar: 'https://readdy.ai/api/search-image?query=powerful%20executive%20CEO%20woman%2045%20confident%20radiant%20smile%20professional%20headshot%20clean%20background%20premium%20quality%20portrait&width=80&height=80&seq=dep-versao-larissa-v1&orientation=squarish',
      estrelas: 5,
    },
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function DepoimentoCard({ dep, cor, borderCard }: { dep: Depoimento; cor: string; borderCard: string }) {
  return (
    <div className={`mt-6 rounded-2xl border ${borderCard} bg-white/3 p-5 relative overflow-hidden`}>
      {/* aspas decorativas */}
      <span className={`absolute -top-1 -left-1 font-display text-7xl leading-none bg-gradient-to-br ${cor} bg-clip-text text-transparent opacity-20 select-none pointer-events-none`}>
        "
      </span>
      {/* estrelas */}
      <div className="flex gap-0.5 mb-3 relative z-10">
        {Array.from({ length: dep.estrelas }).map((_, i) => (
          <i key={i} className="ri-star-fill text-gold-400 text-xs" />
        ))}
      </div>
      <p className="text-cream-100/70 text-sm leading-relaxed font-['Montserrat'] italic mb-4 relative z-10">
        "{dep.texto}"
      </p>
      <div className="flex items-center gap-3 relative z-10">
        <div className="w-9 h-9 rounded-full overflow-hidden border border-white/10 flex-shrink-0">
          <img src={dep.avatar} alt={dep.nome} className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-cream-100 text-xs font-semibold font-['Montserrat']">{dep.nome}</p>
          <p className="text-cream-100/40 text-xs font-['Montserrat']">{dep.cargo}</p>
        </div>
        <div className={`ml-auto text-xs font-semibold font-['Montserrat'] bg-gradient-to-r ${cor} bg-clip-text text-transparent`}>
          Verificado ✓
        </div>
      </div>
    </div>
  );
}

function EtapaCard({ etapa, index }: { etapa: Etapa; index: number }) {
  const { ref, visible } = useInView(0.12);
  const isReversed = index % 2 === 1;

  return (
    <div
      ref={ref}
      className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-0 rounded-3xl overflow-hidden border ${etapa.borderCard} bg-gradient-to-br ${etapa.bgCard} transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {/* Imagem */}
      <div className="lg:w-2/5 relative overflow-hidden min-h-[320px] lg:min-h-[500px]">
        <img
          src={etapa.imagem}
          alt={etapa.titulo}
          className="w-full h-full object-cover object-top absolute inset-0"
        />
        <div
          className={`absolute inset-0 ${
            isReversed
              ? 'bg-gradient-to-l from-charcoal-950/60 via-transparent to-transparent'
              : 'bg-gradient-to-r from-transparent via-transparent to-charcoal-950/60'
          }`}
        />
        <div className="absolute top-6 left-6">
          <span className={`font-display text-7xl font-black bg-gradient-to-br ${etapa.cor} bg-clip-text text-transparent opacity-60 leading-none`}>
            {etapa.numero}
          </span>
        </div>
        <div className="absolute bottom-6 left-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-black/40 backdrop-blur-sm text-white/80 border border-white/15">
            <i className={`${etapa.icone} text-sm`} />
            {etapa.tag}
          </span>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="lg:w-3/5 flex flex-col justify-center p-8 lg:p-12 xl:p-14">
        {/* Ícone + título */}
        <div className="flex items-center gap-4 mb-6">
          <div className={`w-12 h-12 flex items-center justify-center rounded-2xl bg-gradient-to-br ${etapa.cor} flex-shrink-0`}>
            <i className={`${etapa.icone} text-xl text-white`} />
          </div>
          <div>
            <p className="text-cream-100/40 text-xs font-semibold tracking-[0.25em] uppercase font-['Montserrat'] mb-0.5">
              {etapa.subtitulo}
            </p>
            <h3 className="font-display text-3xl lg:text-4xl text-cream-100 leading-tight">
              {etapa.titulo}
            </h3>
          </div>
        </div>

        {/* Descrição */}
        <p className="text-cream-100/60 text-base lg:text-lg leading-relaxed font-['Montserrat'] mb-6">
          {etapa.descricao}
        </p>

        {/* Frase destaque */}
        <div
          className="pl-5 mb-7 relative"
          style={{
            borderLeft: `2px solid transparent`,
            borderImage: `linear-gradient(to bottom, ${
              index === 0 ? '#d97706, #f59e0b' : index === 1 ? '#e11d48, #ec4899' : '#059669, #14b8a6'
            }) 1`,
          }}
        >
          <p className={`text-sm font-semibold font-['Montserrat'] bg-gradient-to-r ${etapa.cor} bg-clip-text text-transparent italic`}>
            "{etapa.destaque}"
          </p>
        </div>

        {/* CTA */}
        <Link
          to={etapa.cta.href}
          className={`self-start inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full text-sm font-semibold font-['Montserrat'] tracking-wide bg-gradient-to-r ${etapa.cor} text-white hover:scale-105 transition-all whitespace-nowrap`}
        >
          <i className={`${etapa.icone} text-base`} />
          {etapa.cta.label}
          <i className="ri-arrow-right-line text-base" />
        </Link>

        {/* Depoimento */}
        <DepoimentoCard dep={etapa.depoimento} cor={etapa.cor} borderCard={etapa.borderCard} />
      </div>
    </div>
  );
}

export default function ProcessoTransformacao() {
  const { ref: headerRef, visible: headerVisible } = useInView(0.2);

  return (
    <section className="relative bg-[#080808] py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-gold-500/4 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-rose-500/3 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 relative z-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 lg:mb-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-10 bg-gradient-to-r from-transparent to-gold-400/60" />
            <span className="text-gold-400 text-xs font-semibold tracking-[0.3em] uppercase font-['Montserrat']">
              O Método em 3 Passos
            </span>
            <div className="h-px w-10 bg-gradient-to-l from-transparent to-gold-400/60" />
          </div>

          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] mb-6">
            Da intenção à sua
            <br />
            <span className="bg-gradient-to-r from-gold-400 via-amber-300 to-gold-500 bg-clip-text text-transparent italic">
              melhor versão
            </span>
          </h2>

          <p className="text-cream-100/50 text-lg lg:text-xl font-['Montserrat'] font-light max-w-2xl mx-auto leading-relaxed">
            Um processo simples, profundo e comprovado para mulheres que querem mais do que resultados — querem uma transformação real e duradoura.
          </p>

          <div className="flex items-center justify-center gap-4 mt-10 flex-wrap">
            {['Carta', 'PNL', 'Transformação'].map((label, i) => (
              <>
                <div key={label} className="flex items-center gap-2 text-cream-100/30 text-xs font-['Montserrat'] tracking-widest uppercase">
                  <span className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-gold-500/60' : i === 1 ? 'bg-rose-500/60' : 'bg-emerald-500/60'}`} />
                  {label}
                </div>
                {i < 2 && (
                  <>
                    <div key={`line-${i}`} className="h-px flex-1 max-w-14 bg-white/10" />
                    <i key={`arr-${i}`} className="ri-arrow-right-line text-cream-100/20 text-sm" />
                    <div key={`line2-${i}`} className="h-px flex-1 max-w-14 bg-white/10" />
                  </>
                )}
              </>
            ))}
          </div>
        </div>

        {/* Cards */}
        <div className="space-y-6 lg:space-y-8">
          {etapas.map((etapa, index) => (
            <EtapaCard key={etapa.numero} etapa={etapa} index={index} />
          ))}
        </div>

        {/* CTA final */}
        <div
          className={`mt-16 text-center transition-all duration-700 delay-500 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-cream-100/40 text-sm font-['Montserrat'] mb-6">
            Mais de 10.000 mulheres já iniciaram essa jornada
          </p>
          <Link
            to="/quiz"
            className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-full font-bold font-['Montserrat'] tracking-wide hover:from-gold-600 hover:to-gold-800 hover:scale-105 transition-all text-sm whitespace-nowrap"
          >
            <i className="ri-sparkling-2-line text-base" />
            Iniciar minha transformação
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
